import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  InputAdornment, CircularProgress, alpha, Grid
} from '@mui/material';
import {
  ArrowForward, ArrowBack, Business, Email, Phone, Person,
  LocationOn, CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const RequestAccess = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    message: ''
  });

  const F = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.companyName.trim()) { setError('Company name is required'); return; }
    if (!form.firstName.trim() || !form.lastName.trim()) { setError('First and last name are required'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }
    if (!form.phone.trim()) { setError('Phone number is required'); return; }
    setError(''); setLoading(true);
    try {
      await authAPI.requestAccess(form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)',
        position: 'relative', overflow: 'hidden'
      }}>
        <Card sx={{
          width: '100%', maxWidth: 480, mx: 2, borderRadius: 4, position: 'relative', zIndex: 1,
          background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
        }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2,
              background: alpha('#10b981', 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <CheckCircle sx={{ fontSize: 40, color: '#10b981' }} />
            </Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', mb: 1 }}>
              Request Submitted!
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#64748b', mb: 3 }}>
              Thank you for your interest in MyPayPlus. Our team will review your request and contact you at <strong>{form.email}</strong> once approved.
            </Typography>
            <Button
              variant="contained" onClick={() => navigate('/login')}
              sx={{
                py: 1.2, px: 4, fontWeight: 700,
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
              }}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)',
      position: 'relative', overflow: 'hidden', py: 4
    }}>
      <Box sx={{
        position: 'absolute', width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
        top: -200, right: -100
      }} />
      <Box sx={{
        position: 'absolute', width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)',
        bottom: -150, left: -100
      }} />

      <Card sx={{
        width: '100%', maxWidth: 540, mx: 2, borderRadius: 4, position: 'relative', zIndex: 1,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 2,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(16,185,129,0.3)'
            }}>
              <Business sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
              Request Access
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.5 }}>
              Submit your information and our team will get back to you
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{
              mb: 2.5, borderRadius: 2.5, fontSize: '0.82rem',
              bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.2)}`,
              '& .MuiAlert-icon': { color: '#ef4444' }
            }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
              Company Name *
            </Typography>
            <TextField fullWidth value={form.companyName} onChange={e => F('companyName', e.target.value)}
              placeholder="e.g. ABC Healthcare Services" autoFocus
              InputProps={{ startAdornment: <InputAdornment position="start"><Business sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ mb: 2 }} />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                  First Name *
                </Typography>
                <TextField fullWidth size="small" value={form.firstName} onChange={e => F('firstName', e.target.value)}
                  placeholder="John"
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                  Last Name *
                </Typography>
                <TextField fullWidth size="small" value={form.lastName} onChange={e => F('lastName', e.target.value)}
                  placeholder="Doe" />
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
              Email *
            </Typography>
            <TextField fullWidth value={form.email} onChange={e => F('email', e.target.value)}
              placeholder="contact@yourbusiness.com"
              InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ mb: 2 }} />

            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
              Phone Number *
            </Typography>
            <TextField fullWidth value={form.phone} onChange={e => F('phone', e.target.value)}
              placeholder="(555) 123-4567"
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ mb: 2 }} />

            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
              Address
            </Typography>
            <TextField fullWidth value={form.address} onChange={e => F('address', e.target.value)}
              placeholder="123 Main Street"
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ mb: 2 }} />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={5}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                  City
                </Typography>
                <TextField fullWidth size="small" value={form.city} onChange={e => F('city', e.target.value)}
                  placeholder="City" />
              </Grid>
              <Grid item xs={3}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                  State
                </Typography>
                <TextField fullWidth size="small" value={form.state} onChange={e => F('state', e.target.value)}
                  placeholder="TX" inputProps={{ maxLength: 2 }} />
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
                  Zip Code
                </Typography>
                <TextField fullWidth size="small" value={form.zip} onChange={e => F('zip', e.target.value)}
                  placeholder="75001" inputProps={{ maxLength: 10 }} />
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
              Additional Message
            </Typography>
            <TextField fullWidth multiline rows={2} value={form.message} onChange={e => F('message', e.target.value)}
              placeholder="Tell us about your business or any questions..."
              sx={{ mb: 3 }} />

            <Button
              fullWidth type="submit" variant="contained" size="large" disabled={loading}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
              sx={{
                py: 1.5, fontWeight: 700, fontSize: '0.9rem',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)', transform: 'translateY(-1px)' }
              }}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>

          <Typography sx={{ textAlign: 'center', mt: 3, fontSize: '0.82rem', color: '#64748b' }}>
            Already have an account?{' '}
            <Typography component="span" onClick={() => navigate('/login')}
              sx={{ color: '#3b82f6', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              Sign In
            </Typography>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RequestAccess;
